import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { api } from "./client";
import type {
  Camera,
  CameraCreateInput,
  CameraList,
  CameraStatusResponse,
  DirectRegisterInput,
  DiscoveryResponse,
  DiscoveryResults,
  PushTokenResponse,
} from "@/types";

const camerasKey = ["cameras"] as const;

export function useCameras(
  opts?: { siteId?: string; status?: string; limit?: number; offset?: number },
) {
  return useQuery({
    queryKey: [...camerasKey, "list", opts ?? {}],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (opts?.siteId) params.set("site_id", opts.siteId);
      if (opts?.status) params.set("status", opts.status);
      if (opts?.limit) params.set("limit", String(opts.limit));
      if (opts?.offset) params.set("offset", String(opts.offset));
      const { data } = await api.get<CameraList>(
        `/api/v1/cameras?${params.toString()}`,
      );
      return data;
    },
  });
}

export function useCamera(
  id: string | undefined,
  options?: Omit<UseQueryOptions<Camera>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: [...camerasKey, "detail", id],
    queryFn: async () => {
      const { data } = await api.get<Camera>(`/api/v1/cameras/${id}`);
      return data;
    },
    enabled: Boolean(id),
    ...options,
  });
}

export function useCameraStatus(id: string | undefined) {
  return useQuery({
    queryKey: [...camerasKey, "status", id],
    queryFn: async () => {
      const { data } = await api.get<CameraStatusResponse>(
        `/api/v1/cameras/${id}/status`,
      );
      return data;
    },
    enabled: Boolean(id),
    refetchInterval: 15_000,
  });
}

export interface CameraStreamResponse {
  camera_id: string;
  hls_url: string | null;
  rtsp_url: string | null;
  mediamtx_path: string | null;
  stream_status: "ready" | "starting" | "offline" | "unknown";
}

// ---------------------------------------------------------------------------
// Sprint 10 — admin-managed cameras + NVR health
// ---------------------------------------------------------------------------
export interface AdminCameraCreateInput {
  name: string;
  location?: string;
  rtsp_url: string;
  audio_enabled?: boolean;
  connection_mode?: "direct_rtsp";
  site_id?: string;
  onvif_host?: string;
  onvif_port?: number;
  onvif_user?: string;
  onvif_pass?: string;
}

export interface AdminCameraCreateResponseT {
  id: string;
  name: string;
  location: string | null;
  stream_key: string;
  hls_url: string;
  rtsp_url: string;
  audio_enabled: boolean;
  is_active: boolean;
  connection_mode: string;
  created_at: string;
  mediamtx_registered: boolean;
}

export function useAdminCreateCamera() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AdminCameraCreateInput) => {
      const { data } = await api.post<AdminCameraCreateResponseT>(
        "/api/v1/cameras/admin",
        input,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: camerasKey });
    },
  });
}

export function useAdminDeleteCamera() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/cameras/admin/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: camerasKey });
    },
  });
}

export interface StreamStatusV2T {
  camera_id: string;
  ready: boolean;
  tracks: string[];
  readers: number;
  source_present: boolean;
  error: string | null;
}

export function useStreamStatusV2(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...camerasKey, "stream-status-v2", id],
    queryFn: async () => {
      const { data } = await api.get<StreamStatusV2T>(
        `/api/v1/cameras/${id}/stream/status`,
      );
      return data;
    },
    enabled: Boolean(id) && enabled,
    refetchInterval: (query) =>
      query.state.data?.ready ? 30_000 : 5_000,
  });
}

export interface NVRHealthSnapshotT {
  id: string;
  camera_id: string;
  checked_at: string;
  hdd_count: number | null;
  hdd_total_gb: number | null;
  hdd_used_gb: number | null;
  hdd_status: "ok" | "warning" | "error" | "unknown" | null;
  hdd_smart_status: "ok" | "warning" | "failed" | "unknown" | null;
  nvr_uptime_sec: number | null;
  stream_status: "ready" | "starting" | "offline" | "unknown";
  error_message: string | null;
}

export interface PollHealthQueued {
  status: "queued";
  task_id: string;
  poll_url: string;
}

// FIX C — POST returns 202; the actual snapshot lands via /health/latest.
export function usePollCameraHealth() {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<PollHealthQueued>(
        `/api/v1/cameras/${id}/health/poll`,
      );
      return data;
    },
  });
}

// FIX C — most recent health snapshot for a camera.
export function useLatestCameraHealth(
  id: string | undefined,
  opts?: { refetchMs?: number; enabled?: boolean },
) {
  return useQuery({
    queryKey: [...camerasKey, "health-latest", id],
    queryFn: async () => {
      const { data } = await api.get<NVRHealthSnapshotT>(
        `/api/v1/cameras/${id}/health/latest`,
      );
      return data;
    },
    enabled: Boolean(id) && (opts?.enabled ?? true),
    refetchInterval: opts?.refetchMs ?? 60_000,
    retry: false,
  });
}

export function useCameraHealthHistory(id: string | undefined, limit = 24) {
  return useQuery({
    queryKey: [...camerasKey, "health-history", id, limit],
    queryFn: async () => {
      const { data } = await api.get<{
        camera_id: string;
        items: NVRHealthSnapshotT[];
      }>(`/api/v1/cameras/${id}/health/history?limit=${limit}`);
      return data;
    },
    enabled: Boolean(id),
    // 90s — health is a slow signal, no need to hammer
    refetchInterval: 90_000,
  });
}


export function useCameraStream(id: string | undefined) {
  return useQuery({
    queryKey: [...camerasKey, "stream", id],
    queryFn: async () => {
      const { data } = await api.get<CameraStreamResponse>(
        `/api/v1/cameras/${id}/stream`,
      );
      return data;
    },
    enabled: Boolean(id),
    // 5s while we're "starting" is short enough to feel responsive; once
    // a stream is ready it doesn't change often, so a quiet poll is fine.
    refetchInterval: (query) =>
      query.state.data?.stream_status === "ready" ? 30_000 : 5_000,
  });
}

export function useCreateCamera() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CameraCreateInput) => {
      const { data } = await api.post<Camera>("/api/v1/cameras", input);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...camerasKey, "list"] });
    },
  });
}

export function useUpdateCamera(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<CameraCreateInput>) => {
      const { data } = await api.patch<Camera>(
        `/api/v1/cameras/${id}`,
        patch,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...camerasKey, "detail", id] });
      void qc.invalidateQueries({ queryKey: [...camerasKey, "list"] });
    },
  });
}

export function useDeleteCamera() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/cameras/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: camerasKey });
    },
  });
}

export function useDispatchDiscovery() {
  return useMutation({
    mutationFn: async (slinkAgentId: string) => {
      const { data } = await api.post<DiscoveryResponse>(
        "/api/v1/cameras/discover",
        { slink_agent_id: slinkAgentId },
      );
      return data;
    },
  });
}

export function useDirectRegister() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: DirectRegisterInput) => {
      const { data } = await api.post<Camera>(
        "/api/v1/cameras/direct-register",
        input,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...camerasKey, "list"] });
    },
  });
}

export function useGeneratePushToken() {
  return useMutation({
    mutationFn: async (cameraId: string) => {
      const { data } = await api.post<PushTokenResponse>(
        `/api/v1/cameras/${cameraId}/generate-push-token`,
      );
      return data;
    },
  });
}

export function useDiscoveryResults(taskId: string | null) {
  return useQuery({
    queryKey: [...camerasKey, "discover", taskId],
    queryFn: async () => {
      const { data } = await api.get<DiscoveryResults>(
        `/api/v1/cameras/discover/${taskId}/results`,
      );
      return data;
    },
    enabled: Boolean(taskId),
    refetchInterval: (query) => {
      const data = query.state.data as DiscoveryResults | undefined;
      if (!data) return 2000;
      if (data.status === "pending" || data.status === "in_progress") {
        return 2000;
      }
      return false;
    },
  });
}
